/* Phase AL2 — Live Alvys status chip for the rental detail page.
 *
 * Mounted next to the "Alvys synced" provenance pill whenever the rental
 * has an ``alvys_trailer_id``. Fetches the read-only status endpoint on
 * mount + on demand, with a small refresh button. Three visual states:
 *
 *  • matching  — green chip "Alvys status: Available"
 *  • diverged  — amber chip with ⚠ icon + expected-status hint
 *  • error     — gray chip with last-known value in tooltip
 *
 * The component never blocks any rental action — it's purely informational.
 * RentFlow does not push status back to Alvys (yet).
 */
import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { RefreshCw, AlertTriangle, CheckCircle2, MinusCircle, Loader2 } from "lucide-react";
import { usePermission } from "@/lib/usePermission";


function _relativeFromNow(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const sec = Math.max(0, Math.round((Date.now() - then) / 1000));
    if (sec < 60) return "just now";
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
}

function _prettyStatus(s) {
    if (!s) return "—";
    // Already a human string like "Available" / "In Use" — surface as-is.
    return String(s);
}


export default function AlvysStatusChip({ alvysTrailerId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const { hasPermission } = usePermission();
    const canRefresh = hasPermission("Alvys TMS", "Refresh trailer sync status");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/alvys/trailers/${alvysTrailerId}/status`);
            setData(data || null);
        } catch (_) {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [alvysTrailerId]);

    useEffect(() => {
        if (!alvysTrailerId) return;
        load();
    }, [alvysTrailerId, load]);

    const onRefresh = async () => {
        if (refreshing) return;
        if (!canRefresh) return;
        setRefreshing(true);
        try {
            const { data } = await api.post(`/admin/alvys/trailers/${alvysTrailerId}/status/refresh`);
            setData(data || null);
        } catch (_) { /* swallow; chip falls back to error state */ }
        finally { setRefreshing(false); }
    };

    if (loading) {
        return (
            <span
                data-testid="alvys-status-chip"
                data-state="loading"
                className="inline-flex items-center gap-1 rounded-full bg-slate-50 text-slate-500 ring-1 ring-slate-200 px-2 py-0.5 text-[10px] font-medium tracking-wide"
            >
                <Loader2 size={10} className="animate-spin" />
                Checking Alvys
            </span>
        );
    }

    // Integration disabled — render nothing (the "Alvys synced" pill
    // is enough provenance on its own).
    if (!data || data.enabled === false) {
        return null;
    }

    const hasError = !!data.error;
    const status = hasError ? data.last_known_status : data.alvys_status;
    const diverged = !!data.diverged && !hasError;
    const fetchedAt = hasError ? data.last_known_fetched_at : data.fetched_at;
    const fresh = _relativeFromNow(fetchedAt);

    let chipClass =
        "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200";
    let Icon = CheckCircle2;
    let state = "matching";
    if (hasError) {
        chipClass = "bg-slate-50 text-slate-500 ring-1 ring-slate-200";
        Icon = MinusCircle;
        state = "error";
    } else if (diverged) {
        chipClass = "bg-amber-50 text-amber-900 ring-1 ring-amber-300";
        Icon = AlertTriangle;
        state = "diverged";
    }

    let label;
    if (hasError) {
        label = status
            ? `Alvys: ${_prettyStatus(status)} (last known)`
            : "Alvys status unavailable";
    } else {
        label = `Alvys: ${_prettyStatus(status)}`;
    }

    const expectedList = Array.isArray(data.expected_alvys_statuses)
        ? data.expected_alvys_statuses
        : [];
    const expectedHint =
        diverged && expectedList.length > 0
            ? ` · expected ${expectedList.slice(0, 3).join(", ")}`
            : diverged
                ? " · this state typically means the rental should be closed"
                : "";

    const tooltip = [
        fresh ? `Last fetched ${fresh}` : null,
        data.source === "cache" ? "cached" : null,
        canRefresh ? "Click to refresh." : "You don't have permission to trigger a sync.",
        "RentFlow does not push status back to Alvys yet.",
        hasError && data.error ? `Provider note: ${data.error}` : null,
        diverged && data.our_status
            ? `Our rental status: ${data.our_status}`
            : null,
    ].filter(Boolean).join(" · ");

    return (
        <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing || !canRefresh}
            data-testid="alvys-status-chip"
            data-state={state}
            data-status={status || ""}
            data-can-refresh={canRefresh ? "1" : "0"}
            title={tooltip}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide ${chipClass} ${canRefresh ? "hover:brightness-95" : "cursor-not-allowed opacity-90"} transition`}
        >
            <Icon size={10} />
            <span>{label}{expectedHint}</span>
            {refreshing
                ? <Loader2 size={10} className="animate-spin ml-0.5" />
                : <RefreshCw size={10} className={`ml-0.5 ${canRefresh ? "opacity-60" : "opacity-30"}`} />}
        </button>
    );
}
